import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AuctionCard from "../components/AuctionCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

export default function Auctions() {
  const params = useQueryParams();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [sort, setSort] = useState(params.get("sort") || "newest");
  const [category, setCategory] = useState(params.get("category_id") || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/categories/tree").then(r => setCategories(r.data)); }, []);
  useEffect(() => {
    setLoading(true);
    const p = { sort, limit: 40 };
    if (q) p.q = q;
    if (category) p.category_id = category;
    if (params.get("featured")) p.featured = true;
    api.get("/listings", { params: p }).then(r => setListings(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [sort, category, q]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Browse</div>
        <h1 className="text-3xl md:text-4xl font-extrabold mt-1">All Auctions</h1>
      </div>

      <div className="flex flex-wrap gap-3 mb-8 items-center">
        <form onSubmit={(e) => { e.preventDefault(); navigate(`/auctions?q=${encodeURIComponent(q)}`); }} className="flex gap-2">
          <Input data-testid="auctions-search-input" placeholder="Search title, brand..." value={q} onChange={(e) => setQ(e.target.value)} className="w-64" />
          <Button data-testid="auctions-search-btn" type="submit" variant="outline">Search</Button>
        </form>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="auctions-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent className="max-h-96">
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => (
              <React.Fragment key={c.id}>
                <SelectItem value={c.id} className="font-semibold">{c.name}</SelectItem>
                {(c.subcategories || []).map(s => (
                  <SelectItem key={s.id} value={s.id} className="pl-6 text-sm text-slate-600">↳ {s.name}</SelectItem>
                ))}
              </React.Fragment>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-52" data-testid="auctions-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="ending_soon">Ending soon</SelectItem>
            <SelectItem value="bids">Most bids</SelectItem>
            <SelectItem value="price_high">Price: high → low</SelectItem>
            <SelectItem value="price_low">Price: low → high</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-slate-500 ml-auto">{listings.length} results</div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8).keys()].map((n) => <div key={`skeleton-${n}`} className="aspect-[4/5] bg-slate-100 dark:bg-slate-800 rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {listings.map(l => <AuctionCard key={l.id} listing={l} />)}
        </div>
      )}
    </div>
  );
}
